import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { toast } from "sonner";
import { ListChecks, EyeOff, Trash2, Check, CheckSquare, Square } from "lucide-react";
import { CategoryBadge, InsightChips, LangBadge, ConfidenceBar, ActionErrorBadge } from "@/components/CommentBits";

export default function ReviewQueue() {
  const { ClientPicker, scopeParam, ready } = useClientScope();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!ready) return;
    const q = scopeParam ? `&${scopeParam}` : "";
    api.get(`/comments?status=needs_review${q}`).then(({ data }) => {
      setItems(data);
      setSelected(new Set());
    }).catch(() => setItems([]));
  }, [scopeParam, ready]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    setBusy(true);
    try {
      await api.post(`/comments/${id}/decision`, { decision });
      toast.success(decision === "approve" ? "Kept" : decision === "delete" ? "Deleted" : "Hidden");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const bulk = async (decision) => {
    if (!selected.size) return;
    setBusy(true);
    try {
      await api.post("/comments/bulk-decision", { ids: [...selected], decision });
      toast.success(`${selected.size} comment(s) ${decision === "approve" ? "kept" : decision === "delete" ? "deleted" : "hidden"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (!items) return;
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">Hybrid mode — human decides</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Review Queue</h1>
          </div>
          <ClientPicker />
        </div>

        {items && items.length > 0 && (
          <div className="flex items-center justify-between bg-card rounded-lg border border-slate-100 px-4 py-2 shadow-sm">
            <button onClick={toggleAll} data-testid="select-all" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600">
              {items && selected.size === items.length ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4" />}
              {selected.size ? `${selected.size} selected` : "Select all"}
            </button>
            <div className="flex items-center gap-2">
              <button disabled={!selected.size || busy} onClick={() => bulk("approve")} data-testid="bulk-approve" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-50 text-green-700 text-xs font-semibold hover:bg-green-100 disabled:opacity-40"><Check className="w-3.5 h-3.5" /> Keep</button>
              <button disabled={!selected.size || busy} onClick={() => bulk("hide")} data-testid="bulk-hide" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 disabled:opacity-40"><EyeOff className="w-3.5 h-3.5" /> Hide</button>
              <button disabled={!selected.size || busy} onClick={() => bulk("delete")} data-testid="bulk-delete" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-800 text-slate-50 text-xs font-semibold hover:bg-slate-900 disabled:opacity-40"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>
        )}

        {items === null ? (
          <div className="space-y-3" aria-busy="true" aria-label="Loading comments">
            {[0, 1, 2].map((i) => <div key={i} className="skeleton h-[104px]" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="bg-card rounded-2xl border border-slate-100 p-12 text-center">
            <ListChecks className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nothing waiting for review. Comments the AI is unsure about will show up here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((c) => (
              <div key={c.id} data-testid="review-item" className={`bg-card rounded-2xl border shadow-card p-4 transition-all ${selected.has(c.id) ? "border-indigo-300 ring-1 ring-indigo-200" : "border-slate-100"}`}>
                <div className="flex gap-3">
                  <button onClick={() => toggle(c.id)} data-testid={`select-${c.id}`} className="mt-1 shrink-0">
                    {selected.has(c.id) ? <CheckSquare className="w-5 h-5 text-indigo-600" /> : <Square className="w-5 h-5 text-slate-300" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="font-semibold text-slate-800 text-sm">{c.author_name}</span>
                      <LangBadge language={c.language} />
                      <CategoryBadge category={c.ai?.category} />
                      <InsightChips insight={c.insight} />
                      <ActionErrorBadge error={c.action_error} />
                      <span className="text-xs text-slate-400">· {c.account_name} · {c.platform}</span>
                    </div>
                    <p className="text-sm text-slate-700 mb-2">{c.text}</p>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <ConfidenceBar value={c.ai?.confidence} />
                        <span className="text-xs text-slate-400 italic max-w-md truncate">{c.ai?.reasoning}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => decide(c.id, "approve")} data-testid={`keep-${c.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-50 text-green-700 text-xs font-semibold hover:bg-green-100"><Check className="w-3.5 h-3.5" /> Keep</button>
                        <button onClick={() => decide(c.id, "hide")} data-testid={`hide-${c.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100"><EyeOff className="w-3.5 h-3.5" /> Hide</button>
                        <button onClick={() => decide(c.id, "delete")} data-testid={`delete-${c.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-800 text-slate-50 text-xs font-semibold hover:bg-slate-900"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
